import { useQueries, UseQueryResult } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import {
  getScansOptions,
  useGetAllRepositoryIntegrationsPages,
  useIntegrationTypesWithInstances,
} from '../../utils/api-client/api-hooks'
import { type ScanAggregateResponse, ToolSchema } from '../../utils/api-client/api-schemas'
import { convertIntegrationTypeToTool, getIsToolIntegrationType } from '../../utils/repository-utils'

export const useGetToolsForRepository = ({ repositoryId }: { repositoryId: string }) => {
  const { data: toolsWithIntegrations, isLoading: isToolsWithIntegrationsLoading } = useGetToolsWithIntegrations()
  const { data: toolsWithRepositoryIntegrations, isLoading: isToolsWithRepositoryIntegrationsLoading } =
    useGetToolsWithRepositoryIntegrations({ repositoryId })
  const { data: toolsWithScans, isLoading: isToolsWithScansLoading } = useGetToolsWithScans({ repositoryId })

  const tools = useMemo(
    () => new Set([...toolsWithIntegrations, ...toolsWithRepositoryIntegrations, ...toolsWithScans]),
    [toolsWithIntegrations, toolsWithRepositoryIntegrations, toolsWithScans]
  )

  return {
    data: tools,
    isLoading: isToolsWithIntegrationsLoading || isToolsWithRepositoryIntegrationsLoading || isToolsWithScansLoading,
  }
}

const useGetToolsWithScans = ({ repositoryId }: { repositoryId: string }) => {
  const queries = ToolSchema.options.map(tool =>
    getScansOptions({
      repositoryIds: [repositoryId],
      tools: [tool],
    })
  )

  const combine = useCallback(
    (results: UseQueryResult<ScanAggregateResponse>[]) => ({
      data: new Set(
        results
          .filter(
            (result): result is Extract<typeof result, { isSuccess: true }> =>
              result.isSuccess && result.data && result.data.total > 0
          )
          .map(result => result.data._embedded.items[0]!.detector)
      ),
      isLoading: results.some(result => result.isLoading),
    }),
    []
  )

  return useQueries({ queries, combine })
}

// "Which tools have an instance" is a question about the whole collection, which
// `useIntegrationTypesWithInstances` answers from `/api/v1/integration-types`. This used to read
// every page of the collection to derive the same set of tool names.
const useGetToolsWithIntegrations = () => {
  const { types, isLoading } = useIntegrationTypesWithInstances()
  const tools = useMemo(
    () => new Set(types.filter(getIsToolIntegrationType).map(convertIntegrationTypeToTool)),
    [types]
  )

  return { data: tools, isLoading }
}

const useGetToolsWithRepositoryIntegrations = ({ repositoryId }: { repositoryId: string }) => {
  const results = useGetAllRepositoryIntegrationsPages({ repositoryId })
  // Not memoized, unlike `useGetToolsWithIntegrations` above: `useGetAllPages` returns a fresh
  // `useQueries` array — of fresh tracked-result proxies — on every render, so a `useMemo` keyed on
  // it would recompute every render anyway. Stabilizing this means giving `useGetAllPages` a
  // `combine`, which needs its own change to that hook's declared return type.
  const tools = new Set(
    results.flatMap(result => result.data?._embedded.items ?? []).map(item => convertIntegrationTypeToTool(item.type))
  )

  return {
    data: tools,
    isLoading: results.some(result => result.isLoading),
  }
}
